import { useState } from 'react';
import { Modal, Button, Input, Textarea } from '../common';
import { updateRestaurant } from '../../services/restaurantService';

/**
 * Onboarding Modal - Marketer fills restaurant details
 * This is the biggest modal with 6 tracked fields
 */
export function OnboardingModal({ restaurant, isOpen, onClose, showToast, currentUser, onUpdateSuccess }) {
  const [formData, setFormData] = useState({
    ownerName: restaurant?.ownerName || "",
    ownerPhone: restaurant?.ownerPhone || "",
    whatsappNumber: restaurant?.whatsappNumber || "",
    taxNumber: restaurant?.taxNumber || "",
    commissionPercentage: restaurant?.commissionPercentage || 15,
    signupFee: restaurant?.signupFee || 150,
    deliveryRadiusKm: restaurant?.deliveryRadiusKm || 0,
    defaultDeliveryFee: restaurant?.defaultDeliveryFee || 0,
    minimumOrderAmount: restaurant?.minimumOrderAmount || 0,
    discountOfferFirst5: restaurant?.discountOfferFirst5 || "",
    openTime: restaurant?.openTime || "09:00",
    closeTime: restaurant?.closeTime || "23:00",
    visitDate: restaurant?.visitDate || "",
    visitTime: restaurant?.visitTime || "",
    visitNotes: restaurant?.visitNotes || "",
    notes: restaurant?.notes || "",
  });
  
  const [submitting, setSubmitting] = useState(false);

  const updateField = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  // Save as Draft - no validation, doesn't change status
  const saveDraft = async () => {
    setSubmitting(true);
    try {
      await updateRestaurant(restaurant.id, formData, currentUser);
      showToast("💾 تم حفظ المسودة بنجاح");
      
      if (onUpdateSuccess) {
        onUpdateSuccess();
      }
    } catch (err) {
      console.error('Draft save error:', err);
      showToast('❌ فشل في حفظ المسودة', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Submit to Data Entry
  const submitToDataEntry = async () => {
    // Validate 6 required tracking fields
    const required = [
      { key: 'ownerName', label: 'اسم المالك' },
      { key: 'ownerPhone', label: 'رقم هاتف المالك' },
      { key: 'taxNumber', label: 'الرقم الضريبي' },
    ];

    const incomplete = required.filter(field => !formData[field.key]);
    
    if (incomplete.length > 0) {
      const fieldNames = incomplete.map(f => f.label).join('، ');
      showToast(`❌ يرجى إكمال: ${fieldNames}`, 'error');
      return;
    }

    setSubmitting(true);
    try {
      await updateRestaurant(
        restaurant.id, 
        { 
          ...formData, 
          status: 'data_review' // Move to next stage
        },
        currentUser
      );
      
      showToast("✅ تم الإرسال إلى مدخل البيانات");
      
      if (onUpdateSuccess) {
        onUpdateSuccess();
      }
      
      onClose();
    } catch (err) {
      console.error('Submit error:', err);
      showToast('❌ فشل في الإرسال', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (!restaurant) return null;

  return (
    <Modal 
      title={`📋 إدخال بيانات: ${restaurant.name}`} 
      isOpen={isOpen} 
      onClose={onClose}
      size="lg"
    >
      <div style={{ direction: "rtl" }}>
        {/* Section 1: Owner Information */}
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ color: "#f1f5f9", fontSize: 16, fontWeight: 700, marginBottom: 16 }}>
            👤 معلومات المالك
          </h3>
          
          <Input
            label="اسم المالك *"
            value={formData.ownerName}
            onChange={e => updateField('ownerName', e.target.value)}
            placeholder="محمد علي"
          />

          <Input
            label="رقم هاتف المالك *"
            type="tel"
            value={formData.ownerPhone}
            onChange={e => updateField('ownerPhone', e.target.value)}
            placeholder="+971 50 123 4567"
          />

          <Input
            label="رقم الواتساب (اختياري)"
            type="tel"
            value={formData.whatsappNumber}
            onChange={e => updateField('whatsappNumber', e.target.value)}
            placeholder="+971 50 123 4567"
          />

          <Input
            label="الرقم الضريبي *"
            value={formData.taxNumber}
            onChange={e => updateField('taxNumber', e.target.value)}
            placeholder="123456789012345"
          />
        </div>

        {/* Section 2: Financial Details */}
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ color: "#f1f5f9", fontSize: 16, fontWeight: 700, marginBottom: 16 }}>
            💰 التفاصيل المالية
          </h3>
          
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Input
              label="نسبة العمولة (%)"
              type="number"
              value={formData.commissionPercentage}
              onChange={e => updateField('commissionPercentage', parseFloat(e.target.value) || 0)}
              placeholder="15"
            />

            <Input
              label="رسوم التسجيل (AED)"
              type="number"
              value={formData.signupFee}
              onChange={e => updateField('signupFee', parseFloat(e.target.value) || 0)}
              placeholder="150"
            />
          </div>
        </div>

        {/* Section 3: Delivery Settings */}
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ color: "#f1f5f9", fontSize: 16, fontWeight: 700, marginBottom: 16 }}>
            🚚 إعدادات التوصيل
          </h3>
          
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <Input
              label="نطاق التوصيل (كم)"
              type="number"
              value={formData.deliveryRadiusKm}
              onChange={e => updateField('deliveryRadiusKm', parseFloat(e.target.value) || 0)}
              placeholder="5"
            />

            <Input
              label="رسوم التوصيل الافتراضية"
              type="number"
              value={formData.defaultDeliveryFee}
              onChange={e => updateField('defaultDeliveryFee', parseFloat(e.target.value) || 0)}
              placeholder="15"
            />

            <Input
              label="الحد الأدنى للطلب"
              type="number"
              value={formData.minimumOrderAmount}
              onChange={e => updateField('minimumOrderAmount', parseFloat(e.target.value) || 0)}
              placeholder="50"
            />
          </div>
        </div>

        {/* Section 4: Visit Scheduling */}
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ color: "#f1f5f9", fontSize: 16, fontWeight: 700, marginBottom: 16 }}>
            📅 جدولة الزيارة
          </h3>
          
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Input
              label="تاريخ الزيارة"
              type="date"
              value={formData.visitDate}
              onChange={e => updateField('visitDate', e.target.value)}
            />

            <Input
              label="وقت الزيارة"
              type="time"
              value={formData.visitTime}
              onChange={e => updateField('visitTime', e.target.value)}
            />
          </div>

          <Textarea
            label="ملاحظات الزيارة"
            value={formData.visitNotes}
            onChange={e => updateField('visitNotes', e.target.value)}
            placeholder="ملاحظات إضافية حول الزيارة..."
            rows={3}
          />
        </div>

        {/* Section 5: Additional Notes */}
        <Textarea
          label="ملاحظات عامة"
          value={formData.notes}
          onChange={e => updateField('notes', e.target.value)}
          placeholder="أي معلومات إضافية..."
          rows={3}
        />

        {/* Action Buttons */}
        <div style={{ display: "flex", gap: 12, marginTop: 24 }}>
          <Button 
            onClick={submitToDataEntry} 
            disabled={submitting}
            style={{ flex: 2, justifyContent: "center" }}
          >
            {submitting ? "جاري الإرسال..." : "✅ إرسال إلى مدخل البيانات"}
          </Button>
          <Button 
            variant="secondary" 
            onClick={saveDraft}
            disabled={submitting}
            style={{ flex: 1, justifyContent: "center" }}
          >
            💾 حفظ المسودة
          </Button>
          <Button 
            variant="secondary" 
            onClick={onClose}
            disabled={submitting}
            style={{ flex: 1, justifyContent: "center" }}
          >
            إلغاء
          </Button>
        </div>
      </div>
    </Modal>
  );
}
